import * as React from "react";
import {
  Badge,
  Box,
  Button,
  Icon,
  Link,
  Text,
} from "@chakra-ui/react";
import { useColorModeValue } from "@chakra-ui/color-mode";
import { FaBuilding } from "react-icons/fa";
import { FiCheck } from "react-icons/fi";
import { TetherLogo } from "../../logo/TetherLogo";

const DEMO_BOOKING_URL = "https://meetings.hubspot.com/julian373";

interface SignupIntentSelectorProps {
  onSelectTetherTick: () => void;
}

export default function SignupIntentSelector({ onSelectTetherTick }: SignupIntentSelectorProps) {
  const handleTetherTickSelect = () => {
    onSelectTetherTick();
  };

  return (
    <Box bg="gray.50">
      <Box display="flex" justifyContent="center" pt={12} pb={8}>
        <Link href="/">
          <TetherLogo height={52} width={196} fill={useColorModeValue("#111928", "white")} />
        </Link>
      </Box>
      <Box maxW="6xl" mx="auto" px={{ base: 4, md: 8 }} mb={12} textAlign="center">
        <Text fontSize="4xl" fontWeight="bold" mb={3}>
          What are you trying to do?
        </Text>
        <Text fontSize="md" color="gray.600">
          Choose from our platform options below.
        </Text>
      </Box>

      <Box maxW="6xl" mx="auto" px={{ base: 4, md: 8 }} pb={16}>
        <Box bg="white" borderRadius="xl" boxShadow="lg" p={6}>
          <Box display="flex" flexDirection="column" gap={8}>
            <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))" gap={6}>
              <Box borderWidth={2} borderColor="gray.200" borderRadius="xl" _hover={{ borderColor: "blue.400" }}>
                <Box display="flex" flexDirection="column" gap={6} p={6}>
                  <Box>
                    <Icon as={FaBuilding} boxSize={8} color="gray.700" mb={3} />
                    <Text fontSize="xl" fontWeight="bold" mb={2}>
                      Tether Platform
                    </Text>
                    <Badge variant="outline" fontSize="xs">
                      Enterprise building management
                    </Badge>
                    <Text fontSize="md" color="gray.600" mt={4}>
                      Monitor energy, water, gas, occupancy, and indoor air quality across buildings.
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={3}>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Icon as={FiCheck} boxSize={5} color="blue.500" mt={0.5} flexShrink={0} />
                      <Text fontSize="sm">
                        See exactly where to save on costs and emissions across your portfolio
                      </Text>
                    </Box>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Icon as={FiCheck} boxSize={5} color="blue.500" mt={0.5} flexShrink={0} />
                      <Text fontSize="sm">Automate ESG reporting aligned to NABERS</Text>
                    </Box>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Icon as={FiCheck} boxSize={5} color="blue.500" mt={0.5} flexShrink={0} />
                      <Text fontSize="sm">Create healthier, more productive spaces</Text>
                    </Box>
                  </Box>

                  <Box pt={2} borderTopWidth={1} borderColor="gray.200">
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
                      BEST FOR
                    </Text>
                    <Text fontSize="sm">
                      Facility managers, sustainability teams, and asset owners managing multiple buildings.
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={3} pt={2}>
                    <Button
                      as="a"
                      href={DEMO_BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Book a 15-minute portfolio review (opens in new tab)"
                      size="lg"
                      colorScheme="blue"
                    >
                      Book a 15-minute portfolio review
                    </Button>
                    <Text textAlign="center" fontSize="sm" color="gray.500">
                      Already have an account?{" "}
                      <Link href="/auth/login" color="blue.500" fontWeight="semibold">
                        Log in
                      </Link>
                    </Text>
                  </Box>
                </Box>
              </Box>

              <Box borderWidth={2} borderColor="gray.200" borderRadius="xl" _hover={{ borderColor: "blue.400" }}>
                <Box display="flex" flexDirection="column" gap={6} p={6}>
                  <Box>
                    <Box
                      as="img"
                      src="/assets/tethertickSVG.svg"
                      alt="Tether Tick"
                      height="48px"
                      objectFit="contain"
                      mb={3}
                    />
                    <Text fontSize="xl" fontWeight="bold" mb={2}>
                      Tether Tick
                    </Text>
                    <Badge variant="outline" fontSize="xs" whiteSpace="normal">
                      Healthy Homes Standards compliance check for rental properties
                    </Badge>
                    <Text fontSize="md" color="gray.600" mt={4}>
                      Check if your home meets New Zealand Healthy Homes Standards.
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={3}>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Icon as={FiCheck} boxSize={5} color="blue.500" mt={0.5} flexShrink={0} />
                      <Text fontSize="sm">Free self assessment in under 5 minutes</Text>
                    </Box>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Icon as={FiCheck} boxSize={5} color="blue.500" mt={0.5} flexShrink={0} />
                      <Text fontSize="sm">Get a clear fix list, for free</Text>
                    </Box>
                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Icon as={FiCheck} boxSize={5} color="blue.500" mt={0.5} flexShrink={0} />
                      <Text fontSize="sm">Certification when you're ready, $59</Text>
                    </Box>
                  </Box>

                  <Box pt={2} borderTopWidth={1} borderColor="gray.200">
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
                      BEST FOR
                    </Text>
                    <Text fontSize="sm">
                      Landlords, homeowners, property managers checking one home (or a small number of homes).
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="column" gap={3} pt={2}>
                    <Button size="lg" colorScheme="blue" onClick={handleTetherTickSelect}>
                      Start free Tether Tick
                    </Button>
                    <Box borderTopWidth={1} borderColor="gray.200" />
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Link
                        href="https://apps.apple.com/us/app/tether-mobile/id1391516440"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={3}
                        bg="black"
                        color="white"
                        borderRadius="lg"
                        px={4}
                        py={3}
                        _hover={{ bg: "blackAlpha.900", textDecoration: "none" }}
                      >
                        <Box as="svg" width="28px" height="28px" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="flex-start">
                          <Text fontSize="10px" lineHeight="short">
                            Download on the
                          </Text>
                          <Text fontSize="lg" fontWeight="semibold" lineHeight="short">
                            App Store
                          </Text>
                        </Box>
                      </Link>
                      <Link
                        href="https://play.google.com/store/apps/details?id=com.tether.installation"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={3}
                        bg="black"
                        color="white"
                        borderRadius="lg"
                        px={4}
                        py={3}
                        _hover={{ bg: "blackAlpha.900", textDecoration: "none" }}
                      >
                        <Box as="svg" width="28px" height="28px" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                        </Box>
                        <Box display="flex" flexDirection="column" alignItems="flex-start">
                          <Text fontSize="10px" lineHeight="short">
                            GET IT ON
                          </Text>
                          <Text fontSize="lg" fontWeight="semibold" lineHeight="short">
                            Google Play
                          </Text>
                        </Box>
                      </Link>
                    </Box>
                    <Text textAlign="center" fontSize="xs" color="gray.500">
                      No credit card required for the free check.
                    </Text>
                    <Text textAlign="center" fontSize="sm" color="gray.500">
                      Already have an account?{" "}
                      <Link href="/auth/login" color="blue.500" fontWeight="semibold">
                        Log in
                      </Link>
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

